import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { triggerWorkflowAutomations } from '@/utils/workflowAutomations';
import { validateFormRequirements, triggerFormAttachments } from '@/utils/formTriggerValidation';
import { toast } from 'sonner';

export const useJobUpdate = () => {
  const [updating, setUpdating] = useState(false);

  const updateJobStatus = async (
    jobId: string,
    newStatus: string,
    workflowType: 'retail' | 'insurance' | 'commercial'
  ) => {
    try {
      setUpdating(true);

      // Get the current job to check old status and org
      const { data: currentJob, error: fetchError } = await supabase
        .from('jobs')
        .select('status, org_id')
        .eq('id', jobId)
        .single();

      if (fetchError) throw fetchError;

      const oldStatus = currentJob.status;

      // Get the workflow stages to find stage IDs
      const { data: workflow } = await supabase
        .from('workflows')
        .select(`
          id,
          workflow_stages (id, stage_key)
        `)
        .eq('org_id', currentJob.org_id)
        .eq('workflow_type', workflowType)
        .maybeSingle();

      const stages = workflow?.workflow_stages as any[] || [];
      const oldStage = stages.find((s: any) => s.stage_key === oldStatus);
      const newStage = stages.find((s: any) => s.stage_key === newStatus);

      // Validate form requirements if we have stage IDs
      if (oldStage && newStage) {
        const validation = await validateFormRequirements(
          jobId,
          oldStage.id,
          newStage.id,
          currentJob.org_id
        );
        
        if (!validation.canProceed) {
          toast.error('Cannot proceed', {
            description: validation.message || 'Required forms must be completed',
          });
          return { success: false, error: validation.message };
        }
      }

      // Update the job status
      const { error: updateError } = await supabase
        .from('jobs')
        .update({ status: newStatus as any })
        .eq('id', jobId);

      if (updateError) throw updateError;

      // Trigger form attachments for the new stage
      if (newStage) {
        await triggerFormAttachments(
          jobId,
          newStage.id,
          'enter_stage',
          currentJob.org_id
        );
      }

      // Trigger workflow automations in the background
      if (oldStatus !== newStatus) {
        triggerWorkflowAutomations({
          jobId,
          oldStatus: oldStatus || '',
          newStatus,
          workflowType,
        }).catch(error => {
          console.error('Background automation failed:', error);
        });
      }

      toast.success('Job status updated');
      
      return { success: true };
    } catch (error) {
      console.error('Error updating job:', error);
      toast.error('Failed to update job status');
      return { success: false, error };
    } finally {
      setUpdating(false);
    }
  };

  return {
    updateJobStatus,
    updating,
  };
};
